/* eslint-disable @next/next/no-img-element */

import { MatchDetailItemFragment, MatchEventFragment } from '@amf/shared/types/graphql';
import { useTranslate } from '@tolgee/react';
import {
  MatchEventType,
  SortedMatchEventSection,
  useSortedMatchEvents,
} from '@amf/shared/data/Match';
import Link from 'next/link';
import React from 'react';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';

interface MatchEventsTableItemProps {
  event: MatchEventFragment;
  prefixes: { player: string };
  facr?: boolean;
}

function MatchEventsTableItem({ event, prefixes, facr }: MatchEventsTableItemProps) {
  const { t } = useTranslate();
  const content = (
    <>
      <div className='MatchEvents__listItemType'>
        {event.type === MatchEventType.goal && (
          <Tippy content={event.title} animation='shift-away' theme='light'>
            <i
              className={classNames('icons-soccer-goal', {
                'MatchRosterList__playerInfo--red': event.isOwnGoal || event.successGoal === false,
              })}
            />
          </Tippy>
        )}
        {event.type === MatchEventType.yellow && !event.redAfterYellowCard && (
          <Tippy content={event.title} animation='shift-away' theme='light'>
            <i className='bi bi-file-fill MatchRosterList__playerInfo--yellow' />
          </Tippy>
        )}
        {event.redAfterYellowCard && (
          <Tippy content={event.title} animation='shift-away' theme='light'>
            <i className='MatchRosterList__playerInfo--redYellow' />
          </Tippy>
        )}
        {event.type === MatchEventType.red && (
          <Tippy content={event.title} animation='shift-away' theme='light'>
            <i className='bi bi-file-fill MatchRosterList__playerInfo--red' />
          </Tippy>
        )}
        {event.type === MatchEventType.penalty && (
          <Tippy content={event.title} animation='shift-away' theme='light'>
            <i className='bi bi-exclamation-diamond-fill MatchRosterList__playerInfo--red' />
          </Tippy>
        )}
      </div>
      <div className='MatchEvents__listItemPlayer'>
        {event.player?.fullName ?? event.message ?? t('match.event.player.empty')}
        {event.player?.facr && facr ? <> (FAČR)</> : null}
        {event.assistant && <span> ({event.assistant.fullName})</span>}
        {event.type === MatchEventType.goal && event.isGoalFromPenalty && <span> (penalta)</span>}
      </div>
      {!event.penaltyMethod && (
        <div className='MatchEvents__listItemTime'>{event.printableMinute}</div>
      )}
    </>
  );

  if (!event.player) {
    return <div className='MatchEvents__listItem'>{content}</div>;
  }

  return (
    <Link href={`${prefixes.player}/${event.player?.id}`}>
      <a className='MatchEvents__listItem'>{content}</a>
    </Link>
  );
}

interface MatchEventsTableSectionProps {
  section: SortedMatchEventSection;
  prefixes: { team: string; player: string };
  type: 'home' | 'visiting';
  facr?: boolean;
}

function MatchEventsTableSection({ section, prefixes, type, facr }: MatchEventsTableSectionProps) {
  const { t } = useTranslate();
  return (
    <div className='MatchEvents__section'>
      <Link href={`${prefixes.team}/${section.team.id}`}>
        <a className='MatchRosterList__team'>
          <h4 className='MatchRosterList__teamName'>
            {section.team.club.brandUrl && (
              <img
                className='MatchRosterList__teamBrand'
                src={section.team.club.brandUrl}
                alt={section.team.club.name}
              />
            )}
            <span>{section.team.name}</span>
          </h4>
          <span className='MatchRosterList__teamType'>{t(`match.roster.${type}`)}</span>
        </a>
      </Link>
      <div className='MatchEvents__list'>
        <h5 className='MatchEvents__listTitle'>{t('match.event.section.goals')}</h5>
        {section.goals.standard.length === 0 && section.goals.extension.length === 0 && (
          <div className='MatchEvents__listEmpty'>{t('match.event.empty.goals')}</div>
        )}
        {section.goals.standard.map(event => (
          <MatchEventsTableItem key={event.id} event={event} prefixes={prefixes} facr={facr} />
        ))}
        {section.goals.extension.map(event => (
          <MatchEventsTableItem key={event.id} event={event} prefixes={prefixes} facr={facr} />
        ))}
      </div>
      {section.goals.penalty.length > 0 && (
        <div className='MatchEvents__list'>
          <h5 className='MatchEvents__listTitle'>{t('match.event.section.penalty')}</h5>
          {section.goals.penalty.map(event => (
            <MatchEventsTableItem key={event.id} event={event} prefixes={prefixes} facr={facr} />
          ))}
        </div>
      )}
      <div className='MatchEvents__list'>
        <h5 className='MatchEvents__listTitle'>{t('match.event.section.other')}</h5>
        {section.other.standard.length === 0 &&
          section.other.extension.length === 0 &&
          section.other.penalty.length === 0 && (
            <div className='MatchEvents__listEmpty'>{t('match.event.empty.other')}</div>
          )}
        {section.other.standard.map(event => (
          <MatchEventsTableItem key={event.id} event={event} prefixes={prefixes} facr={facr} />
        ))}
        {section.other.extension.map(event => (
          <MatchEventsTableItem key={event.id} event={event} prefixes={prefixes} facr={facr} />
        ))}
        {section.other.penalty.map(event => (
          <MatchEventsTableItem key={event.id} event={event} prefixes={prefixes} facr={facr} />
        ))}
      </div>
    </div>
  );
}

interface MatchEventsTableProps {
  match: MatchDetailItemFragment;
  homeTeamId: string;
  prefixes: { team: string; player: string };
  facr?: boolean;
}

function MatchEventsTable({ match, homeTeamId, prefixes, facr }: MatchEventsTableProps) {
  const events = useSortedMatchEvents(match, homeTeamId);

  return (
    <div className='MatchEvents__table'>
      <MatchEventsTableSection section={events.home} prefixes={prefixes} type='home' facr={facr} />
      <MatchEventsTableSection
        section={events.visiting}
        prefixes={prefixes}
        type='visiting'
        facr={facr}
      />
    </div>
  );
}

export default MatchEventsTable;
